import type { SalonInvoiceProfile } from '../../services/peInvoiceFirebaseService';
import type {
  InvoiceCalculations,
  InvoiceCustomer,
  InvoiceItem,
  InvoiceMeta,
  InvoiceShipping,
} from '../../store/usePEInvoiceStore';
import type { CoachEInvoiceProfile } from '../../types/coachEInvoiceProfile';
import { PREISEXPERT_EINVOICE_BUYER } from '../../types/coachEInvoiceProfile';
import { isValidIban } from '../pe-papers/ibanValidation';

export interface CoachEInvoiceCompleteness {
  complete: boolean;
  eInvoiceReady: boolean;
  missing: string[];
}

const hasText = (v: unknown) => String(v ?? '').trim().length > 0;

/** "33609 Bielefeld" → PLZ + Ort (wie in Team-Rechnungsfeldern). */
export function parseInvoiceCityLine(invoiceCity: unknown): { zip: string; city: string } {
  const t = String(invoiceCity ?? '').trim();
  const m = t.match(/^(\d{4,5})\s+(.+)$/);
  if (m) return { zip: m[1], city: m[2].trim() };
  return { zip: '', city: t };
}

/** "305/5037/4231 | USt-Id: DE298727966" → getrennte Steuerkennungen. */
export function parseCombinedTaxId(raw: unknown): { taxNumber: string; vatId: string } {
  const s = String(raw ?? '').trim();
  if (!s || s === '-') return { taxNumber: '', vatId: '' };
  const vatMatch = s.match(/\b(DE[A-Z0-9]{9,})\b/i);
  const vatId = vatMatch ? vatMatch[1].toUpperCase() : '';
  let taxNumber = s
    .replace(/USt-?Id\.?\s*:?\s*DE[A-Z0-9]{9,}/gi, '')
    .replace(/\bDE[A-Z0-9]{9,}\b/gi, '')
    .replace(/\|/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (taxNumber === '-') taxNumber = '';
  return { taxNumber, vatId };
}

/**
 * Seller-Profil aus dem Coach-Objekt der Honorarabrechnung (gleiches Mapping wie PDF-Visual).
 * Nicht aus dem eingeloggten Admin-Profil — sonst landet PreisExpert im SellerTradeParty.
 */
export function coachMemberToEInvoiceProfile(
  coach: Record<string, unknown> | null | undefined
): CoachEInvoiceProfile {
  const c = coach || {};
  const displayName = String(c.invoiceCompanyName || c.name || '').trim();
  const accountHolder = String(c.invoiceAccountHolder || c.name || displayName).trim();
  const { zip, city } = parseInvoiceCityLine(c.invoiceCity);
  const { taxNumber, vatId } = parseCombinedTaxId(c.invoiceTaxId);
  return {
    displayName,
    accountHolder,
    bankName: String(c.invoiceBankName || '').trim(),
    iban: String(c.invoiceIban || '').trim(),
    bic: String(c.invoiceBic || '').trim(),
    taxNumber,
    vatId,
    street: String(c.invoiceStreet || '').trim(),
    zip,
    city,
    country: 'DE',
    email: String(c.email || c.coachEmail || '').trim(),
    phone: String(c.phone || c.invoicePhone || '').trim(),
    reverseCharge: c.invoiceReverseCharge === true,
    defaultPaymentTermsDays: 14,
  };
}

export function assessCoachEInvoiceProfile(profile: CoachEInvoiceProfile): CoachEInvoiceCompleteness {
  const missing: string[] = [];
  if (!hasText(profile.displayName)) missing.push('Firmenname / Name');
  if (!hasText(profile.street)) missing.push('Straße');
  if (!hasText(profile.zip)) missing.push('PLZ');
  if (!hasText(profile.city)) missing.push('Ort');
  if (!hasText(profile.email)) missing.push('E-Mail');
  if (!hasText(profile.accountHolder)) missing.push('Kontoinhaber');
  if (!isValidIban(profile.iban)) missing.push('Gültige IBAN');
  if (!hasText(profile.taxNumber) && !hasText(profile.vatId)) {
    missing.push('Steuernummer oder USt-IdNr.');
  }
  const complete = missing.length === 0;
  return { complete, eInvoiceReady: complete, missing };
}

export function toSalonInvoiceProfileFromCoach(profile: CoachEInvoiceProfile): SalonInvoiceProfile {
  const displayName = String(profile.displayName || '').trim();
  const accountHolder = String(profile.accountHolder || displayName).trim();
  return {
    accountHolder,
    bankName: profile.bankName,
    iban: profile.iban,
    bic: profile.bic,
    taxNumber: profile.taxNumber,
    vatId: profile.vatId,
    invoicePrefix: 'COACH-',
    defaultPaymentTermsDays: profile.defaultPaymentTermsDays || 14,
    senderCompanyName: displayName || accountHolder,
    senderStreet: profile.street,
    senderZip: profile.zip,
    senderCity: profile.city,
    senderCountry: profile.country || 'DE',
    senderEmail: profile.email,
    senderPhone: profile.phone,
  };
}

export function applyCoachEInvoiceToMemberFields(
  member: Record<string, unknown> | null | undefined,
  profile: CoachEInvoiceProfile
): Record<string, unknown> {
  const base = { ...(member || {}) };
  if (!assessCoachEInvoiceProfile(profile).complete) return base;
  return {
    ...base,
    invoiceCompanyName: profile.displayName,
    invoiceAccountHolder: profile.accountHolder || profile.displayName,
    invoiceStreet: profile.street,
    invoiceCity: `${profile.zip} ${profile.city}`.trim(),
    invoiceTaxId: profile.vatId || profile.taxNumber,
    invoiceIban: profile.iban,
    invoiceBic: profile.bic,
    invoiceReverseCharge: profile.reverseCharge === true,
    email: profile.email || base.email,
  };
}

export function buildPreisExpertBuyerCustomer(): InvoiceCustomer {
  return {
    type: 'business',
    firstName: '',
    lastName: '',
    companyName: PREISEXPERT_EINVOICE_BUYER.companyName,
    contactPerson: PREISEXPERT_EINVOICE_BUYER.contactPerson,
    vatId: PREISEXPERT_EINVOICE_BUYER.vatId,
    buyerReference: '',
    email: PREISEXPERT_EINVOICE_BUYER.email,
    street: PREISEXPERT_EINVOICE_BUYER.street,
    zip: PREISEXPERT_EINVOICE_BUYER.zip,
    city: PREISEXPERT_EINVOICE_BUYER.city,
    country: PREISEXPERT_EINVOICE_BUYER.country,
  };
}

export function buildClientBuyerCustomer(client: {
  companyName?: string;
  firmenname?: string;
  fullName?: string;
  email?: string;
  street?: string;
  zip?: string;
  city?: string;
  country?: string;
  clientData?: { strasse?: string; plz?: string; ort?: string; land?: string; ustId?: string };
}): InvoiceCustomer {
  const d = client.clientData || {};
  const land = String(client.country || d.land || 'DE').trim();
  const country =
    land.toUpperCase() === 'DEUTSCHLAND' || land.toUpperCase() === 'D'
      ? 'DE'
      : land.slice(0, 2).toUpperCase() || 'DE';
  return {
    type: 'business',
    firstName: '',
    lastName: '',
    companyName: client.companyName || client.firmenname || client.fullName || 'Mandant',
    contactPerson: client.fullName || '',
    vatId: d.ustId || '',
    buyerReference: '',
    email: client.email || '',
    street: client.street || d.strasse || '',
    zip: client.zip || d.plz || '',
    city: client.city || d.ort || '',
    country,
  };
}

export type CoachZugferdBundle = {
  profile: SalonInvoiceProfile;
  customer: InvoiceCustomer;
  items: InvoiceItem[];
  shipping: InvoiceShipping;
  meta: InvoiceMeta;
  calculations: InvoiceCalculations;
};

export function buildCoachHonorariumZugferdPayload(opts: {
  profile: CoachEInvoiceProfile;
  invoiceNumber: string;
  invoiceDateIso: string;
  targetMonthText: string;
  netTotal: number;
  vatTotal: number;
  grossTotal: number;
  reverseCharge?: boolean;
}): CoachZugferdBundle {
  const rc = opts.reverseCharge === true || opts.profile.reverseCharge === true;
  const vatRate = rc ? 0 : 19;
  return {
    profile: toSalonInvoiceProfileFromCoach(opts.profile),
    customer: buildPreisExpertBuyerCustomer(),
    items: [
      {
        id: 'coach-honorar-1',
        kind: 'service',
        description: `Honorarabrechnung ${opts.targetMonthText}`,
        quantity: 1,
        unit: 'Pausch.',
        unitPrice: opts.netTotal,
        priceMode: 'net',
        vatRate,
        serviceHours: 0,
        hourlyRate: 0,
        materialMode: 'exclusive',
      },
    ],
    shipping: { enabled: false, amount: 0, vatRate },
    meta: {
      docType: 'invoice',
      invoiceNumber: opts.invoiceNumber,
      invoiceDate: opts.invoiceDateIso,
      deliveryDate: opts.invoiceDateIso,
      paymentDeadlineDays: opts.profile.defaultPaymentTermsDays || 14,
      notes: rc ? 'Steuerschuldnerschaft des Leistungsempfängers (§ 13b UStG).' : '',
    },
    calculations: {
      netTotal: opts.netTotal,
      grossTotal: opts.grossTotal,
      vatTotal: opts.vatTotal,
      standardVatRate: vatRate,
      isVatExempt: rc,
      vatBreakdown: {
        standard: { rate: vatRate, net: opts.netTotal, vat: opts.vatTotal },
      },
    },
  };
}

export function buildCoachClientZugferdPayload(opts: {
  profile: CoachEInvoiceProfile;
  customer: InvoiceCustomer;
  invoiceNumber: string;
  invoiceDateIso: string;
  description: string;
  netTotal: number;
  vatTotal: number;
  grossTotal: number;
  vatRate: number;
  /** Ausland: keine deutsche MwSt. ausweisen (nicht MwSt-Befreiung) */
  reverseCharge?: boolean;
}): CoachZugferdBundle {
  const rc = opts.reverseCharge === true || opts.vatRate === 0;
  const rate = rc ? 0 : opts.vatRate || 19;
  return {
    profile: toSalonInvoiceProfileFromCoach(opts.profile),
    customer: opts.customer,
    items: [
      {
        id: 'coach-client-1',
        kind: 'service',
        description: opts.description || 'Consulting / Coaching',
        quantity: 1,
        unit: 'Pausch.',
        unitPrice: opts.netTotal,
        priceMode: 'net',
        vatRate: rate,
        serviceHours: 0,
        hourlyRate: 0,
        materialMode: 'exclusive',
      },
    ],
    shipping: { enabled: false, amount: 0, vatRate: rate },
    meta: {
      docType: 'invoice',
      invoiceNumber: opts.invoiceNumber,
      invoiceDate: opts.invoiceDateIso,
      deliveryDate: opts.invoiceDateIso,
      paymentDeadlineDays: opts.profile.defaultPaymentTermsDays || 14,
      notes: rc
        ? 'Steuerschuldnerschaft des Leistungsempfängers (Reverse-Charge / Ausland) — keine deutsche MwSt. ausgewiesen.'
        : '',
    },
    calculations: {
      netTotal: opts.netTotal,
      grossTotal: opts.grossTotal,
      vatTotal: opts.vatTotal,
      standardVatRate: rate,
      isVatExempt: rc,
      vatBreakdown: {
        standard: { rate, net: opts.netTotal, vat: opts.vatTotal },
      },
    },
  };
}
